import {
  buildPageEvent,
  buildIdentifyEvent,
  GeoLocation,
  NinetailedRequestContext,
  NinetailedApiClient,
  SelectedVariantInfo,
  Traits,
} from '@ninetailed/experience.js-shared';
import { v4 as uuid } from 'uuid';
import { RequestCookies } from 'next/dist/compiled/@edge-runtime/cookies';
import serverContext from 'server-only-context';
import { NINETAILED_ANONYMOUS_ID_COOKIE } from 'utils/cookies';
import { isDraftPreviewEnabled } from 'utils/draftPreview';
import { NinetailedMode } from 'types/constants';
import { isE2ETesting } from 'utils/environment';
import { PageContext } from 'utils/pageContext';
import { readRedisCache, writeRedisCache } from 'utils/redis/redisCache';
import { PAGE_HAS_EXPERIMENTS_TTL_SECONDS, RedisKey } from 'utils/redis/redisConfig';
import { isExperimentPipelineV2Enabled } from 'libs/statsig';
import { logPipelineEvent, now } from 'libs/experimentation/observability';

export type SelectedVariant = { experienceId: string; variantIndex: number };

// Lazy singleton — reuses TCP connections across requests via native fetch.
// Lazy to avoid triggering the constructor at import time (breaks test files
// that transitively import this module without mocking the shared package).
let ntApiClient: NinetailedApiClient;
function getNtApiClient(): NinetailedApiClient {
  if (!ntApiClient) {
    ntApiClient = new NinetailedApiClient({
      clientId: process.env.NEXT_PUBLIC_NINETAILED_API_KEY ?? '',
      environment: 'main',
    });
  }
  return ntApiClient;
}

type SendPagePayload = {
  ctx: NinetailedRequestContext;
  cookies: RequestCookies;
  ip?: string;
  location?: GeoLocation;
  queryParams?: string;
  expectExperiments?: boolean;
};

export const createRequestContext = (request: Request): NinetailedRequestContext => {
  return {
    url: request.url,
    locale: 'en-US', // One would make this dynamic if handling multiple locales
    referrer: request.headers.get('referer') || '',
    userAgent: request.headers.get('user-agent') || '',
  };
};

export const sendPageEvent = async ({
  ctx,
  cookies,
  ip,
  location,
  queryParams,
  expectExperiments,
}: SendPagePayload) => {
  let ninetailedId = cookies.get(NINETAILED_ANONYMOUS_ID_COOKIE)?.value;
  if (!ninetailedId) {
    // If no ninetailedId, generate a new one using a 64-character hex string
    const chars = '0123456789abcdef';
    ninetailedId = Array.from({ length: 64 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
  }

  const traits: Traits = { query_params: queryParams || '' };

  // Send identify event via upsertManyProfiles (the /events endpoint),
  // matching the Node client's sendIdentifyEvent behavior:
  // - Empty request context (not the page's ctx)
  // - anonymousId field set to the userId
  // Awaited sequentially so traits are set before audience bucketing.
  const identifyEvent = buildIdentifyEvent({
    ctx: { url: '', referrer: '', locale: '', userAgent: '' },
    messageId: uuid(),
    timestamp: Date.now(),
    userId: ninetailedId,
    traits,
  });

  let identifyFailed = false;
  try {
    await getNtApiClient().upsertManyProfiles(
      { events: [{ ...identifyEvent, anonymousId: ninetailedId }] },
      { timeout: 2000 },
    );
  } catch (error) {
    identifyFailed = true;
    console.error('Failed to send identify:', error);
  }

  const pageEvent = buildPageEvent({
    ctx,
    messageId: uuid(),
    timestamp: Date.now(),
    properties: {},
  });

  const pagePath = extractPath(ctx.url);
  const startedAt = now();
  try {
    const result = await getNtApiClient().upsertProfile(
      {
        profileId: ninetailedId,
        events: [{ ...pageEvent, context: { ...pageEvent.context, location } }],
      },
      { ip, timeout: 2000 },
    );

    logPipelineEvent({
      event: 'proxy.ninetailedCall',
      vendor: 'ninetailed',
      durationMs: now() - startedAt,
      selectionCount: result.experiences.length,
    });

    if (result.experiences.length === 0 && !identifyFailed && expectExperiments) {
      console.warn(`[Ninetailed] API returned 0 experiences for ${pagePath}`);
    }

    return result;
  } catch (error) {
    logPipelineEvent({
      event: 'proxy.ninetailedCall',
      vendor: 'ninetailed',
      fallbackReason: 'adapter_failure',
      durationMs: now() - startedAt,
      error,
    });
    console.error(`[Ninetailed] API error for ${pagePath}:`, error, {
      profileId: ninetailedId.slice(0, 8),
      identifyFailed,
      ip: ip ?? 'unknown',
    });
    return { profile: { id: ninetailedId }, experiences: [] };
  }
};

function extractPath(url: string): string {
  try {
    return new URL(url).pathname;
  } catch {
    return url;
  }
}

export const encodeExperienceSelections = (selections: SelectedVariantInfo[]): string => {
  return selections
    .map(selection => {
      return {
        experienceId: selection.experienceId,
        variantIndex: selection.variantIndex,
      };
    })
    .map(selection => {
      return `${selection.experienceId}${encodeURIComponent('=')}${selection.variantIndex}`;
    })
    .sort()
    .join(encodeURIComponent(','));
};

export const decodeExperienceSelections = (encodedExperienceVariantsMap: string): Record<string, number> => {
  return encodedExperienceVariantsMap
    .split(encodeURIComponent(','))
    .map(experienceIdWithVariant => {
      const [experienceId, _variantIndex] = experienceIdWithVariant.split(encodeURIComponent('='));

      const variantIndex = parseInt(_variantIndex);

      if (!experienceId || variantIndex === undefined || isNaN(variantIndex)) {
        return null;
      }

      return { experienceId, variantIndex };
    })
    .filter((x): x is SelectedVariant => !!x)
    .reduce((acc, curr) => ({ ...acc, [curr.experienceId]: curr.variantIndex }), {});
};

export const [getExperiences, setExperiences] = serverContext<Record<string, number> | null>(null);
export const [getPageHasExperiments, setPageHasExperiments] = serverContext<boolean>(false);

export const getNinetailedMode: () => Promise<NinetailedMode> = async () => {
  // We use this to override the mode when needed
  // It's handy for local development in .env.local
  // or if we need to override and entire environment like QA
  const overrideMode = process.env.NEXT_PUBLIC_NINETAILED_MODE;

  // If an override mode is set and valid, use it
  if (overrideMode === 'server' || overrideMode === 'client') {
    return overrideMode;
  }

  // Otherwise determine the rendering mode based on whether the env enables draft preview
  return (await isDraftPreviewEnabled()) ? 'client' : 'server';
};

export async function getSlugAndExperiences({ params }: { params: Promise<{ slug: string[] }> }) {
  const rawSlug = (await params)?.slug || [];
  const slug = rawSlug.join('/');

  // importing headers here in-line allows
  // this function to be used both client + server-side
  const { headers } = await import('next/headers');
  const headersList = await headers();

  // Get experience selections from header
  const experienceSelections = headersList.get('x-ninetailed-experiences');
  const selectedExperiences = experienceSelections ? decodeExperienceSelections(experienceSelections) : null;

  return {
    selectedExperiences,
    slug,
  };
}

/**
 * Whether Statsig experiments are enabled for this page.
 * Currently only the referral page (/r/...) uses Statsig for hero experiments.
 *
 * To be removed when Experiment Pipeline V2 is fully rolled out.
 */
export async function isStatsigExperiment(headers?: PageContext): Promise<boolean> {
  if (headers == undefined || headers?.anonymousId == undefined) {
    return false;
  }
  // V2 pipeline uses per-module type guards (hasStatsigContainer) instead of URL routing
  if (await isExperimentPipelineV2Enabled()) {
    return false;
  }
  return headers.url.includes('/r/');
}

/**
 * Whether Ninetailed experiments are enabled for this page.
 * Ninetailed is always enabled. It coexists with Statsig on pages that use both.
 */
export async function isNinetailedExperiment(_headers?: PageContext): Promise<boolean> {
  return true;
}

/**
 * Determines whether to force baseline rendering for Ninetailed experiences.
 * This function centralizes all conditions that should force baseline rendering.
 */
export function shouldRenderBaseline(): boolean {
  // Always render baseline in CI environments to prevent flaky tests or unintended VRT failures
  if (isE2ETesting()) {
    return true;
  }

  // Future conditions can be added here:
  // - Feature flags (e.g., disable personalization for certain users)
  // - User preferences (e.g., opt-out of personalization)
  // - Performance considerations (e.g., disable on slow connections)

  return false;
}

function normalizePageHasExperimentsSlug(slug: string): string {
  if (slug === '/') {
    return slug;
  }

  return slug.startsWith('/') ? slug.slice(1) : slug;
}

export async function getCachedPageHasExperiments(slug: string): Promise<boolean | null> {
  try {
    return await readRedisCache<boolean>(normalizePageHasExperimentsSlug(slug), RedisKey.PageHasExperiments);
  } catch {
    return null;
  }
}

export async function cachePageHasExperiments(slug: string, hasExperiments: boolean): Promise<void> {
  try {
    await writeRedisCache(
      normalizePageHasExperimentsSlug(slug),
      hasExperiments,
      RedisKey.PageHasExperiments,
      PAGE_HAS_EXPERIMENTS_TTL_SECONDS,
    );
  } catch {
    // best-effort cache only; page render should not fail if Redis is unavailable
  }
}
